import { getCommunicationsByCustomerId } from '@/lib/demo-data/customer-communications-contacts';
import { mapCommunicationApiToUi } from '@/lib/mappers/customer-detail';
import type { CommunicationsApiResponse, StubResponse } from '@/types/api/customer-detail';
import { isStubResponse } from '@/types/api/customer-detail';
import type { CommunicationUI } from '@/types/ui/customer-detail';
import { useApi } from './useApi';
import { useModeAwareQuery } from './useModeAwareQuery';

/**
 * Mode-aware query hook for fetching customer communications.
 * Returns communications reactively via useModeAwareQuery, respecting the
 * current screen's Demo/Live toggle.
 *
 * @param customerId - The customer ID to fetch communications for
 * @param source - Optional source filter (e.g. "Dispute", "Promise", "Case")
 */
export function useCustomerCommunications(customerId: string, source?: string) {
  const { apiGet } = useApi();
  const mockCommunications = getCommunicationsByCustomerId(customerId).map(mapCommunicationApiToUi);

  return useModeAwareQuery<CommunicationUI[], Error>(
    ['customer-communications', customerId, source ?? 'all'],
    async ({ signal }) => {
      if (!customerId || customerId === '0') return [];
      const queryParam = source && source !== 'all' ? `?source=${encodeURIComponent(source)}` : '';
      const response = await apiGet<CommunicationsApiResponse | StubResponse>(
        `/api/v1/customers/${customerId}/communications${queryParam}`,
        undefined,
        signal,
      );

      if (isStubResponse(response)) {
        return [];
      }

      return response.communications.map(mapCommunicationApiToUi);
    },
    mockCommunications,
    {
      enabled: !!customerId && customerId !== '0',
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}
